import { BMSkeletonLoader, BMText } from "@traveloka/district-bloom";
import { memo, useCallback, useMemo } from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { StyleSheet } from "react-native";

import BMView from "../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { FlightPrebookFormValue } from "../types";
import FlightPrebookCrossSellAddOnsError from "./FlightPrebookCrossSellAddOnsError";
import FlightPrebookCrossSellAddOnsItem from "./FlightPrebookCrossSellAddOnsItem";

type Props = {
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  handleMoreInfoPress?: (link: string) => void;
};

export default function FlightPrebookCrossSellAddOnsList({
  isError,
  isLoading,
  refetch,
  handleMoreInfoPress,
}: Props) {
  const { CorporateFlightPrebookForm: cr } = useMobileContentResource();
  const { control } = useFormContext<FlightPrebookFormValue>();
  const crossSellProductAddOnsObj =
    useWatch({ control, name: "crossSellProductAddOns" }) || {};

  const optionsIds: string[] = useMemo(() => {
    return Object.keys(crossSellProductAddOnsObj);
  }, [crossSellProductAddOnsObj]);

  if (!isLoading && !isError && !optionsIds.length) return null;

  return (
    <BMView spacing="md">
      <BMText variant="uiHuge">{cr.addOnsFormTitle}</BMText>
      {isLoading ? <BMSkeletonLoader height={114} style={styles.flex} /> : null}
      {!isLoading && isError ? (
        <FlightPrebookCrossSellAddOnsError onRetry={refetch} />
      ) : null}
      {!isLoading && !isError
        ? optionsIds.map((optionId) => (
            <CrossSellAddOnRow
              key={optionId}
              name={`crossSellProductAddOns.${optionId}`}
              handleMoreInfoPress={handleMoreInfoPress}
            />
          ))
        : null}
    </BMView>
  );
}

type RowProps = {
  name: `crossSellProductAddOns.${string}`;
  handleMoreInfoPress?: (link: string) => void;
};

/**
 * Row component for each add-on item in the list
 * @param name - The name of the field in the form
 */
const CrossSellAddOnRow = memo(function CrossSellAddOnRow({
  name,
  handleMoreInfoPress,
}: RowProps) {
  const {
    field: {
      value: { checkBoxInfo },
      onChange,
    },
  } = useController<FlightPrebookFormValue, `crossSellProductAddOns.${string}`>(
    { name }
  );

  const onSelectAddOn = useCallback(() => {
    if (!checkBoxInfo) return;
    onChange({
      checkBoxInfo: {
        ...checkBoxInfo,
        checkBoxDisplayInfo: {
          ...checkBoxInfo.checkBoxDisplayInfo,
          isChecked: !checkBoxInfo.checkBoxDisplayInfo.isChecked,
        },
      },
    });
  }, [checkBoxInfo]);

  if (!checkBoxInfo) return null;

  return (
    <FlightPrebookCrossSellAddOnsItem
      item={checkBoxInfo.checkBoxDisplayInfo}
      onCheckOption={onSelectAddOn}
      handleMoreInfoPress={handleMoreInfoPress}
    />
  );
});

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
});
